/**
 * 成果价值评估模块配置
 * 支持多语言切换
 */
import { deepCloneConfig } from '@/utils/deepCloneConfig'
import { applyPortalZhToRu } from '@/utils/applyPortalZhToRu'
import { L, resolveLocaleImages } from '@/utils/localeAsset'

// 中文配置
const zhCN = {
  // 网站信息
  siteInfo: {
    title: '杨凌农业科技成果转化综合信息服务平台',
    logo: L('shouye/web-logo.png'),
    loginText: '登录',
    registerText: '注册'
  },

  // 成果价值评估列表页
  valueAssessment: {
    title1: '成果价值',
    title2: '评估',
    subtitle: '多元评估 立体转化',
    searchPlaceholder: '请输入关键词搜索',
    searchButton: '搜索',
    evaluateButton: '我要评估',

    breadcrumb: {
      home: '首页',
      current: '成果价值评估',
      detail: '评估详情'
    },

    filters: {
      typeLabel: '成果类别:',
      subCategoryLabel: '成果子类:',
      fieldLabel: '所属领域:',
      timeLabel: '时　间:',
      scoreLabel: '评　分:',
      maturityLabel: '成熟度:'
    },

    card: {
      priceLabel: '预期区间',
      viewDetail: '查看详情',
      scoreLabel: '评分'
    },

    pagination: {
      pageInfo: '共{total}条，每页显示{pageSize}条',
      firstPage: '首页',
      prevPage: '上一页',
      nextPage: '下一页',
      lastPage: '末页'
    },

    assets: {
      bannerBg: L('kejirencai/head-bg@2x.png'),
      filterActionBg: L('valueAssessment/bg.png'),
      evaluateBtnBg: L('valueAssessment/button_bg.png'),
      evaluateBtnIcon: L('valueAssessment/icon.png')
    },

    detail: {
      statusFinished: '评估完成',
      scoreLabel: '评估得分',
      scoreUnit: '分',
      priceLabel: '预期区豆',
      contactText: '立即对接',
      collect: '收藏',
      collected: '已收藏：',
      collectUnit: '次',
      meta: {
        type: '成果类别：',
        tech: '所属领域：',
        maturity: '成熟度：'
      },
      infoSection: {
        title: '成果信息',
        subtitle: 'ACHIEVEMENT INFORMATION',
        organization: '所属单位',
        maturity: '成熟度',
        applicationField: '成果介绍'
      },
      detailSection: {
        title: '评估详情',
        subtitle: 'EVALUATION DETAILS',
        maturityTitle: '【成熟期】',
        yieldTitle: '【产量】',
        qualityTitle: '【品质】',
        resistanceTitle: '【抗性】',
        adaptabilityTitle: '【适应性评估区域】',
        varietyRightTitle: '【品种权】',
        profitModelTitle: '【盈利模式】',
        weight: '权重占比：',
        quantScore: '量化函数得分：',
        finalScore: '该项条件得分：',
        maturityLabel: '【成熟度】',
        techMeans: '【技术手段】',
        indicators: '【待修饰指标】',
        fieldPerformance: '【田间表现】',
        comparisonStandard: '【晒比标准】',
        regionVerification: '【区域验证】',
        standingQuality: '【灯立品质】',
        nutrientQuality: '【营物品质】',
        storageQuality: '【储存品质】',
        diseaseResistance: '【抗病性】',
        adversityAdaptability: '【逆境适应性】',
        adaptabilityDesc: '适应性描述',
        varietyRightDesc: '【评价等平】',
        profitModelDesc: '盈利模式描述'
      }
    }
  },

  // 功能模块
  modules: [
    { id: 'home', name: '首页', icon: '', link: '/index' },
    { id: 'innovation', name: '走进杨凌', icon: '', link: '/innovation' },
    { id: 'supply', name: '供需中心', icon: '', link: '/supply-demand' },
    { id: 'evaluation', name: '价值评估', icon: '', link: '/value-assessment' },
    { id: 'manager', name: '技术经理人', icon: '', link: '/manager' },
    { id: 'transaction', name: '交易中心', icon: '', link: '/transaction' },
    { id: 'service', name: '科创服务', icon: '', link: '/innovation-service' },
    { id: 'talent', name: '科技人才', icon: '', link: '/talent' },
    { id: 'enterprise', name: '农业企业', icon: '', link: '/agriculture-enterprise' },
    { id: 'policy', name: '政策信息', icon: '', link: '/policy' }
  ],

  // 页脚信息
  footer: {
    copyright: '',
    support: '技术支持：浪潮软件科技有限公司'
  }
}

// 英文配置（简化）
const enUS = {
  siteInfo: {
    title: 'Yangling Agriculture Technology Achievements Transformation Comprehensive Information Platform',
    logo: L('shouye/web-logo.png'),
    loginText: 'Login',
    registerText: 'Register'
  },
  modules: [
    { id: 'home', name: 'Home', icon: '', link: '/index' },
    { id: 'innovation', name: 'Innovation Yangling', icon: '', link: '/innovation' },
    { id: 'supply', name: 'Supply & Demand', icon: '', link: '/supply-demand' },
    { id: 'evaluation', name: 'Value Assessment', icon: '', link: '/value-assessment' },
    { id: 'manager', name: 'Tech Managers', icon: '', link: '/manager' },
    { id: 'transaction', name: 'Transaction Center', icon: '', link: '/transaction' },
    { id: 'service', name: 'Innovation Services', icon: '', link: '/innovation-service' },
    { id: 'talent', name: 'Tech Talent', icon: '', link: '/talent' },
    { id: 'enterprise', name: 'Agricultural Enterprises', icon: '', link: '/agriculture-enterprise' },
    { id: 'policy', name: 'Policy', icon: '', link: '/policy' }
  ],
  footer: {
    copyright: '',
    support: 'Technical Support: Inspur Software Technology Co., Ltd.'
  },
  valueAssessment: {
    title1: 'Value',
    title2: 'Assessment',
    subtitle: 'Multi-dimensional assessment, integrated transformation',
    searchPlaceholder: 'Please enter keywords',
    searchButton: 'Search',
    evaluateButton: 'Evaluate Now',
    breadcrumb: {
      home: 'Home',
      current: 'Value Assessment',
      detail: 'Assessment Detail'
    },
    filters: {
      typeLabel: 'Category:',
      subCategoryLabel: 'Subcategory:',
      fieldLabel: 'Field:',
      timeLabel: 'Time:',
      scoreLabel: 'Score:',
      maturityLabel: 'Maturity:'
    },
    card: {
      priceLabel: 'Expected Price',
      viewDetail: 'View Detail',
      scoreLabel: 'Score'
    },
    pagination: {
      pageInfo: 'Total {total}, {pageSize} per page',
      firstPage: 'First',
      prevPage: 'Prev',
      nextPage: 'Next',
      lastPage: 'Last'
    },
    assets: {
      bannerBg: L('kejirencai/head-bg@2x.png'),
      filterActionBg: L('valueAssessment/bg.png'),
      evaluateBtnBg: L('valueAssessment/button_bg.png'),
      evaluateBtnIcon: L('valueAssessment/icon.png')
    },
    detail: {
      statusFinished: 'Finished',
      scoreLabel: 'Score',
      scoreUnit: 'pts',
      priceLabel: 'Expected Price',
      contactText: 'Contact Now',
      collect: 'Favorite',
      collected: 'Favorited:',
      collectUnit: 'times',
      meta: {
        type: 'Category:',
        tech: 'Field:',
        maturity: 'Maturity:'
      },
      infoSection: {
        title: 'Achievement Info',
        subtitle: 'ACHIEVEMENT INFORMATION',
        organization: 'Organization',
        maturity: 'Maturity',
        applicationField: 'Achievement Introduction'
      },
      detailSection: {
        title: 'Evaluation Details',
        subtitle: 'EVALUATION DETAILS',
        maturityTitle: '[Maturity]',
        yieldTitle: '[Yield]',
        qualityTitle: '[Quality]',
        resistanceTitle: '[Resistance]',
        adaptabilityTitle: '[Adaptability]',
        varietyRightTitle: '[Variety Right]',
        profitModelTitle: '[Profit Model]',
        weight: 'Weight:',
        quantScore: 'Quantified Score:',
        finalScore: 'Final Score:',
        maturityLabel: '[Maturity]',
        techMeans: '[Tech Means]',
        indicators: '[Indicators]',
        fieldPerformance: '[Field Performance]',
        comparisonStandard: '[Comparison Standard]',
        regionVerification: '[Regional Verification]',
        standingQuality: '[Standing Quality]',
        nutrientQuality: '[Nutrient Quality]',
        storageQuality: '[Storage Quality]',
        diseaseResistance: '[Disease Resistance]',
        adversityAdaptability: '[Adversity Adaptability]',
        adaptabilityDesc: 'Adaptability',
        varietyRightDesc: '[Variety Right]',
        profitModelDesc: 'Profit Model'
      }
    }
  }
}

const ruRU = applyPortalZhToRu(deepCloneConfig(zhCN))

// 当前语言配置
let currentLang = 'zh-CN'

// 语言映射
const langMap = {
  'zh-CN': zhCN,
  'en-US': enUS,
  'ru-RU': ruRU
}

export function getValueAssessmentConfig(lang) {
  const locale = lang || currentLang
  const config = langMap[locale] || zhCN
  return resolveLocaleImages(config, locale)
}

export function setLanguage(lang) {
  if (langMap[lang]) {
    currentLang = lang
  }
}

export function getCurrentLanguage() {
  return currentLang
}

export default {
  getValueAssessmentConfig,
  setLanguage,
  getCurrentLanguage
}
